import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useParams } from 'react-router-dom';

import { useChecklist } from 'api/useChecklist';
import { useSelfServe } from 'api/useSelfServe';
import { useUserDetails } from 'api/useUserDetails';
import {
  ChecklistTask,
  EChecklistTaskStatus,
  EPublisherSolutionModel,
  EPublisherSolutionModelSubType,
  ETaskId,
  Project
} from 'common/contracts';
import {
  addPermissionBasedLocking,
  getCategoryStatus,
  selfServeChecklistInitialCategories,
  webCheckoutChecklistInitialCategories,
  webStoreChecklistInitialCategories} from 'pages/Layout/SetupChecklist/constants';
import { ISetupChecklistCategory } from 'pages/Layout/SetupChecklist/types';

import { useObservable } from '../observable';
import { setupChecklistStateService } from '../setup-checklist.state.service';

export const useChecklistState = (shouldFetchTasks: boolean = true) => {
  const { publisherId } = useParams();
  const categories = useObservable(setupChecklistStateService.categories);
  const tasks = useObservable(setupChecklistStateService.tasks);
  const isExpanded = useObservable(setupChecklistStateService.isExpanded);
  const shouldWidgetBeShown = useObservable(setupChecklistStateService.shouldWidgetBeShown);
  const { userDetails } = useUserDetails();

  const resolvedPublisherId = useMemo(() => {
    if (publisherId) return publisherId;
    return userDetails?.projects?.find(
      ({ projectType }: Project) => projectType === EPublisherSolutionModel.CHECKOUT
    )?.publisherId;
  }, [publisherId, userDetails?.projects]);

  const currentProject = useMemo(
    () => userDetails?.projects?.find(
      (project: Project) => project.publisherId === resolvedPublisherId
    ),
    [userDetails?.projects, resolvedPublisherId]
  );

  const isWebStore = useMemo(
    () => currentProject?.projectType === EPublisherSolutionModel.STORE,
    [currentProject?.projectType]
  );

  const isWebCheckout = useMemo(
    () => currentProject?.projectType === EPublisherSolutionModel.CHECKOUT && 
           currentProject?.projectSubType === EPublisherSolutionModelSubType.WEB_CHECKOUT,
    [currentProject?.projectType, currentProject?.projectSubType]
  );

  const isPaymentLinks = useMemo(
    () => currentProject?.projectType === EPublisherSolutionModel.CHECKOUT && 
           currentProject?.projectSubType === EPublisherSolutionModelSubType.PAYMENT_LINKS,
    [currentProject?.projectType, currentProject?.projectSubType]
  );

  const { checkTestPaymentCompletion } = useSelfServe(resolvedPublisherId);
  const { updateTask, getTasks } = useChecklist(resolvedPublisherId, shouldFetchTasks && shouldWidgetBeShown);
  const hasCheckedTestPayment = useRef(false);

  useEffect(() => {
    if (getTasks.data && getTasks.data.length > 0 && !hasCheckedTestPayment.current && !isWebStore) {
      const testPaymentStatus = getTasks.data.find(
        ({ id }) => id === ETaskId.TEST_PAYMENTS
      )?.status;

      if (testPaymentStatus !== EChecklistTaskStatus.DONE) {
        hasCheckedTestPayment.current = true;
        checkTestPaymentCompletion(updateTask);
      }
    }
  }, [getTasks.data, checkTestPaymentCompletion, isWebStore, updateTask]);

  const setTasksFromStatusList = useCallback((statusList?: ChecklistTask[]) => {
    if (!statusList || !statusList.length) {
      setupChecklistStateService.setCategories([]);
      return;
    }

    const taskStatusMap = Object.fromEntries(
      statusList.map((task) => [task.id, task.status])
    );

    const checklistCategories = isWebStore 
      ? webStoreChecklistInitialCategories 
      : isWebCheckout
      ? webCheckoutChecklistInitialCategories
      : selfServeChecklistInitialCategories;

    const categoriesWithPermissions = (isWebStore || isWebCheckout)
      ? addPermissionBasedLocking(checklistCategories, resolvedPublisherId || '')
      : checklistCategories;

    const populatedCategories = categoriesWithPermissions
      .map((section) => {
        const validTasks = section.tasks
          .filter((task) => taskStatusMap[task.id])
          .map((task) => ({
            ...task,
            status: taskStatusMap[task.id]
          }));

        if (validTasks.length === 0) return null;

        const taskStatuses = validTasks.map((t) => t.status);
        const sectionStatus = getCategoryStatus(taskStatuses);

        return {
          ...section,
          tasks: validTasks,
          status: sectionStatus
        };
      })
      .filter(Boolean);

    const allTasks = populatedCategories
      .flatMap((category) => category?.tasks || []);

    setupChecklistStateService.setCategories(
      populatedCategories as ISetupChecklistCategory[]
    );
    setupChecklistStateService.setTasks(allTasks);

  }, [isWebStore, isWebCheckout, resolvedPublisherId]);

  const hasCategories = useMemo(() => {
    return !!categories?.length && categories.length > 0;
  }, [categories]);

  const setTaskStatus = (id: ETaskId, status?: EChecklistTaskStatus) => {
    const targetStatus = status || EChecklistTaskStatus.DONE;

    const currentTask = getTasks.data?.find(task => task.id === id);
    if (currentTask?.status === targetStatus) {
      return;
    }

    updateTask.mutate(
      { id, status: targetStatus },
      {
        onSuccess: () => {
          getTasks.refetch();
        }
      }
    );
  };

  return {
    hasCategories,
    categories,
    tasks,
    isExpanded,
    isWebStore,
    isWebCheckout,
    isPaymentLinks,
    selectedCategory: useObservable(
      setupChecklistStateService.selectedCategory
    ),
    isSendAgreementModalOpen: useObservable(
      setupChecklistStateService.isSendAgreementModalOpen
    ),
    isVerifyBusinessModalOpen: useObservable(
      setupChecklistStateService.isVerifyBusinessModalOpen
    ),
    setIsExpanded: setupChecklistStateService.setIsExpanded,
    setIsVerifyBusinessModalOpen:
      setupChecklistStateService.setIsVerifyBusinessModalOpen,
    setIsSendAgreementModalOpen:
      setupChecklistStateService.setIsSendAgreementModalOpen,
    setSelectedCategory: setupChecklistStateService.setSelectedCategory,
    setTasksFromStatusList,
    setTaskStatus,
    toggleChecklistExpandedState: (forceState?: boolean) =>
      setupChecklistStateService.toggleChecklistExpandedState(
        getTasks.refetch,
        forceState
      ),
    resetSelectedCategory: setupChecklistStateService.resetSelectedCategory
  };
};
